import type { MagicLinkRow } from "./api";

export type LinkStatusFilter = "active" | "expired" | "all";
export type LinkDateFilter = "week" | "month" | "quarter" | "year" | "all";

export const EXPIRED_HISTORY_DAYS = 90;
const EXPIRED_HISTORY_MS = EXPIRED_HISTORY_DAYS * 24 * 60 * 60 * 1000;

/** Expired links remain auditable in storage but leave the Settings history
 * after 90 days. Active and revoked links are never silently discarded here. */
export function isVisibleMagicLink(link: MagicLinkRow, now = Date.now()): boolean {
  if (link.status !== "expired" || !link.expiresAt) return true;
  return new Date(link.expiresAt).getTime() >= now - EXPIRED_HISTORY_MS;
}

export function filterMagicLinks(
  links: MagicLinkRow[],
  filter: LinkStatusFilter,
  now = Date.now()
): MagicLinkRow[] {
  return links
    .filter((link) => isVisibleMagicLink(link, now))
    .filter((link) => filter === "all" || link.status === filter)
    .sort((a, b) => {
      if (filter === "all" && a.status !== b.status) {
        if (a.status === "active") return -1;
        if (b.status === "active") return 1;
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
}

const DATE_WINDOW_DAYS: Record<Exclude<LinkDateFilter, "all">, number> = {
  week: 7,
  month: 30,
  quarter: 90,
  year: 365,
};

/** Narrow the already-authorized Magic Link audit view by consultant and age.
 * This is display-only filtering: it never changes retention or link state. */
export function filterMagicLinksForReview(
  links: MagicLinkRow[],
  consultantQuery: string,
  dateFilter: LinkDateFilter,
  now = Date.now()
): MagicLinkRow[] {
  const term = consultantQuery.trim().toLocaleLowerCase();
  const cutoff =
    dateFilter === "all" ? null : now - DATE_WINDOW_DAYS[dateFilter] * 24 * 60 * 60 * 1000;

  return links.filter((link) => {
    const consultantMatches =
      !term ||
      link.recipientName.toLocaleLowerCase().includes(term) ||
      link.recipientEmail.toLocaleLowerCase().includes(term);
    const dateMatches = cutoff === null || new Date(link.createdAt).getTime() >= cutoff;
    return consultantMatches && dateMatches;
  });
}
